import React, { useState, useEffect, useMemo, useRef } from 'react'
import _ from 'lodash'
import { Button, message, Modal, Icon } from 'antd'
import './ImageGeneration.scss'
import StepTitle from '../StepTitle'
import ProviderSelectionModal from './components/ProviderSelectionModal'
import SceneContainer from './components/SceneContainer'
import SettingsDrawer from './components/SettingsDrawer'
import ImageEditorModal from './components/ImageEditorModal'
import ConfirmationModal from './components/ConfirmationModal'
import AddShotModal from './components/AddShotModal'
import ImageGenerationSummary from './components/GenerationSummary/ImageGenerationSummary'
import { generateId } from './utils/helpers'
import { useVoiceOver } from './hooks/useVoiceOver'
import {
  normalizeSceneAudioHistory,
  restoreSceneAudioVersion
} from '../../utils/audioHistory'

const ImageGeneration = ({
  formData,
  setFormData,
  onValidationChange,
  onGoBack,
  isClusteringAudio,
  imageProvider,
  isImageGenerationLoading: isLoading,
  setIsImageGenerationLoading: setIsLoading,
  generateAllImages,
  handleRegenerateImage,
  handleSelectReferenceAndRegenerate: regenerateWithReference,
  retryFailedImages,
  onNavigateToStep,
  audioClusteringProgress,
  audioClusteringStatus,
  videoId,
  handleFileUpload,
  isUploadingImage,
  isFetchingCharacters,
  onStopGeneration
}) => {
  const scenes = formData.shotList || []

  const setScenes = action => {
    setFormData(prev => {
      const currentScenes = prev.shotList || []
      const newScenes =
        typeof action === 'function' ? action(currentScenes) : action
      return { ...prev, shotList: newScenes }
    })
  }

  const [selectedSettings, setSelectedSettings] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [showProviderModal, setShowProviderModal] = useState(false)

  const [showImageEditorModal, setShowImageEditorModal] = useState(false)
  const [imageEditorMode, setImageEditorMode] = useState('kontext')
  const [currentEditingShot, setCurrentEditingShot] = useState(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmationModalConfig, setConfirmationModalConfig] = useState({})

  const [showAddShotModal, setShowAddShotModal] = useState(false)
  const [shotViewMode, setShotViewMode] = useState('expanded')
  const [addShotContext, setAddShotContext] = useState({
    sceneId: null,
    isAddingScene: false
  })

  const updateShotDataRef = useRef(
    _.debounce((shotId, key, value) => {
      setScenes(prevScenes =>
        prevScenes.map(scene => ({
          ...scene,
          shots: scene.shots.map(shot =>
            shot.id === shotId ? { ...shot, [key]: value } : shot
          )
        }))
      )
    }, 500)
  )

  const updateSceneData = (sceneId, key, value) => {
    setScenes(prevScenes =>
      prevScenes.map(scene =>
        scene.id === sceneId ? { ...scene, [key]: value } : scene
      )
    )
  }

  const updateScene = (sceneId, updater) => {
    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id !== sceneId) return scene
        return typeof updater === 'function' ? updater(scene) : updater
      })
    )
  }

  const handleSceneStructureChange = updatedScene => {
    if (!updatedScene || !updatedScene.id) return
    setScenes(prevScenes =>
      prevScenes.map(scene =>
        scene.id === updatedScene.id ? { ...scene, ...updatedScene } : scene
      )
    )
  }

  const handleRestoreAudio = (sceneId, audioVersion) => {
    updateScene(sceneId, scene => restoreSceneAudioVersion(scene, audioVersion))
    message.success('Audio version restored')
  }

  const {
    voices = [],
    loadingVoices,
    fetchVoices,
    generateVoiceForScene,
    isAudioPolling,
    audioProgress,
    audioStatus,
    audioMessage,
    stopAudioPolling
  } = useVoiceOver({
    scenes,
    updateSceneData,
    updateScene,
    videoId
  })
  const activeAudioGenerationProgress = isAudioPolling
    ? audioProgress
    : audioClusteringProgress
  const activeAudioGenerationStatus = isAudioPolling
    ? audioMessage || audioStatus
    : audioClusteringStatus
  const handleStopSceneAudioGeneration = () => {
    if (isAudioPolling) {
      stopAudioPolling()
      return
    }

    if (onStopGeneration) {
      onStopGeneration()
    }
  }

  const hasCheckedInitialImagesRef = useRef(false)
  const lastProviderRef = useRef(null)
  const isGeneratingRef = useRef(false)

  useEffect(() => {
    fetchVoices()
  }, [])

  // Check for missing images when provider is selected/changed
  useEffect(() => {
    if (!imageProvider) {
      hasCheckedInitialImagesRef.current = false
      lastProviderRef.current = null
      isGeneratingRef.current = false
      return
    }

    // Reset check flag if provider actually changed
    if (lastProviderRef.current !== imageProvider) {
      hasCheckedInitialImagesRef.current = false
      lastProviderRef.current = imageProvider
      isGeneratingRef.current = false
    }

    // Only generate images once when provider is first set or changes
    // Skip if we've already checked or if we're currently generating
    if (!hasCheckedInitialImagesRef.current && !isGeneratingRef.current) {
      hasCheckedInitialImagesRef.current = true

      if (formData.shotList && formData.shotList.length > 0) {
        // Check if there are any shots missing images or marked as generating (interrupted)
        const hasMissingImages = formData.shotList.some(
          scene =>
            scene.shots &&
            scene.shots.length > 0 &&
            scene.shots.some(shot => !shot.imageSrc || shot.isGenerating)
        )

        // Only generate if there are actually missing images
        if (hasMissingImages) {
          isGeneratingRef.current = true
          generateAllImages(formData.shotList, true).finally(() => {
            isGeneratingRef.current = false
          })
        } else {
          // All images already exist, just set loading to false
          setIsLoading(false)
        }
      } else {
        // No shot list yet, set loading to false
        setIsLoading(false)
      }
    }
  }, [imageProvider])

  useEffect(() => {
    if (isLoading) return
    const allImagesGenerated = scenes.every(scene =>
      scene.shots.every(shot => shot.imageSrc)
    )
    onValidationChange(allImagesGenerated)
  }, [scenes, isLoading])

  const handleRegenerateWithReference = (sceneId, shotId) => {
    setCurrentEditingShot({ sceneId, shotId })
    setImageEditorMode('kontext')
    setShowImageEditorModal(true)
  }

  const handleRegenerateWithReferenceNanoBanana = (sceneId, shotId) => {
    setCurrentEditingShot({ sceneId, shotId })
    setImageEditorMode('nanobanana')
    setShowImageEditorModal(true)
  }

  const handleImageEditorConfirm = imageUrl => {
    if (!currentEditingShot || !imageUrl) return

    const { sceneId, shotId } = currentEditingShot

    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId
                ? {
                    ...shot,
                    imageSrc: imageUrl,
                    images_history: [
                      ...(shot.images_history || []),
                      { url: imageUrl, timestamp: Date.now() }
                    ]
                  }
                : shot
            )
          }
        }
        return scene
      })
    )

    setShowImageEditorModal(false)
    setCurrentEditingShot(null)
    message.success('Image updated successfully')
  }

  const handleEditSettings = (sceneId, shotId) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene.shots.find(sh => sh.id === shotId)

    setSelectedSettings({
      sceneId,
      shotId,
      ...scene.settings,
      ...shot
    })
    setDrawerVisible(true)
  }

  const updateSettings = (key, value) => {
    setSelectedSettings(prev => ({ ...prev, [key]: value }))
  }

  const saveSettings = () => {
    Modal.confirm({
      title: 'Confirm Changes',
      content:
        'Are you sure you want to save these settings? This will regenerate the image.',
      centered: true,
      onOk: () => {
        setScenes(prevScenes =>
          prevScenes.map(scene => {
            if (scene.id === selectedSettings.sceneId) {
              return {
                ...scene,
                shots: scene.shots.map(shot =>
                  shot.id === selectedSettings.shotId
                    ? {
                        ...shot,
                        initial_image_prompt:
                          selectedSettings.initial_image_prompt,
                        voiceover_text: selectedSettings.voiceover_text,
                        lighting: selectedSettings.lighting,
                        scenery: selectedSettings.scenery,
                        imageSrc: selectedSettings.imageSrc,
                        images_history: selectedSettings.images_history
                      }
                    : shot
                )
              }
            }
            return scene
          })
        )
        setDrawerVisible(false)

        const { sceneId, shotId } = selectedSettings
        handleRegenerateImage(sceneId, shotId)
        message.info('Image is regenerating...')
      }
    })
  }

  const handleSortEnd = (sceneId, { oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return
    const arrayMove = (array, from, to) => {
      const item = array[from]
      const newArray = [...array]
      newArray.splice(from, 1)
      newArray.splice(to, 0, item)
      return newArray
    }
    setScenes(prev =>
      prev.map(scene =>
        scene.id === sceneId
          ? { ...scene, shots: arrayMove(scene.shots, oldIndex, newIndex) }
          : scene
      )
    )
  }

  const handleCopyImageToShot = (
    sourceSceneId,
    sourceShotId,
    targetSceneId,
    targetShotId
  ) => {
    const sourceScene = scenes.find(scene => scene.id === sourceSceneId)
    const sourceShot = sourceScene?.shots?.find(shot => shot.id === sourceShotId)
    const targetScene = scenes.find(scene => scene.id === targetSceneId)
    const targetShot = targetScene?.shots?.find(shot => shot.id === targetShotId)

    if (!sourceShot?.imageSrc) {
      message.warning('This shot does not have an image to copy yet.')
      return
    }

    if (!targetShot) {
      message.warning('Target shot not found.')
      return
    }

    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id !== targetSceneId) return scene

        return {
          ...scene,
          shots: scene.shots.map(shot => {
            if (shot.id !== targetShotId) return shot

            return {
              ...shot,
              imageSrc: sourceShot.imageSrc,
              isGenerating: false,
              isUploading: false,
              progress: 100,
              error: null,
              images_history: [
                ...(shot.images_history || []),
                { url: sourceShot.imageSrc, timestamp: Date.now() }
              ]
            }
          })
        }
      })
    )

    message.success(
      `Copied image to ${targetScene?.title || 'target scene'}${
        targetShot?.title ? ` - ${targetShot.title}` : ''
      }`
    )
  }

  const createEmptyShot = () => {
    const id = generateId('shot-')
    return {
      id,
      key: id,
      title: 'New Shot',
      initial_image_prompt: '',
      video_prompt: '',
      dialogue: '',
      isGenerating: false,
      progress: 0,
      imageSrc: '',
      is_static: false
    }
  }

  const createEmptyScene = () => ({
    id: generateId('scene-'),
    title: 'New Scene',
    description: '',
    audio_link: null,
    audio_duration: null,
    audio_history: [],
    shots: [createEmptyShot()]
  })

  const hasVidSrc = scene => {
    return scene?.shots?.some(shot => shot.vidSrc)
  }

  const showDurationWarning = (title, content, onConfirm) => {
    setConfirmationModalConfig({
      title: title || 'Confirm Changes',
      content: content,
      onConfirm: () => {
        onConfirm()
        setShowConfirmationModal(false)
      }
    })
    setShowConfirmationModal(true)
  }

  const addShotToScene = sceneId => {
    setAddShotContext({ sceneId, isAddingScene: false })
    setShowAddShotModal(true)
  }

  const addNewSceneWithShot = () => {
    setAddShotContext({ sceneId: null, isAddingScene: true })
    setShowAddShotModal(true)
  }

  const handleAddShotConfirm = values => {
    const { sceneTitle, shots } = values

    const getNextShotNumber = (existingShots = []) => {
      const maxIndex = existingShots.reduce((max, shot) => {
        if (!shot?.id) return max
        const match = shot.id.match(/-shot-(\d+)$/)
        return match ? Math.max(max, parseInt(match[1], 10)) : max
      }, 0)
      return maxIndex + 1
    }

    const processShot = (shot, forcedId) => {
      const {
        shotTitle,
        imageDescription,
        location,
        shotSize,
        videoDescription,
        camera,
        emotionalTone,
        cameraMotion,
        duration,
        is_static = false
      } = shot

      const initial_image_prompt = `${location}, ${shotSize} of ${imageDescription}, [SHOT SIZE: ${shotSize}], photorealistic, 8K`
      const video_prompt = `${videoDescription}, [CAMERA: ${camera}], [DURATION: ${duration} seconds], [EMOTIONAL TONE: ${emotionalTone}], [MOTION: ${cameraMotion}]`

      const id = forcedId || generateId('shot-')
      return {
        ...createEmptyShot(),
        id,
        key: id,
        title: shotTitle,
        initial_image_prompt,
        video_prompt,
        is_static
      }
    }

    if (addShotContext.isAddingScene) {
      const newSceneId = generateId('scene-')
      const newShots = shots.map((shot, index) => {
        const shotId = `${newSceneId}-shot-${index + 1}`
        return processShot(shot, shotId)
      })

      const newScene = {
        id: newSceneId,
        title: sceneTitle || `Scene ${scenes.length + 1}`,
        description: values.sceneDescription || '',
        audio_link: null,
        audio_duration: null,
        audio_history: [],
        shots: newShots
      }
      setScenes(prev => [...prev, normalizeSceneAudioHistory(newScene)])
      message.success('New scene added with shots')
    } else {
      const sceneId = addShotContext.sceneId
      const scene = scenes.find(s => s.id === sceneId)

      const nextNumber = getNextShotNumber(scene?.shots || [])
      const newShotId = `${sceneId}-shot-${nextNumber}`
      const newShot = processShot(shots[0], newShotId)

      const performAdd = () => {
        setScenes(prev =>
          prev.map(s =>
            s.id === sceneId
              ? { ...s, shots: [...(s.shots || []), newShot] }
              : s
          )
        )
        message.success('Shot added')
      }

      if (hasVidSrc(scene)) {
        showDurationWarning(
          'Add New Shot',
          "You are adding a new shot to a scene that already has generated videos.\n\n1. Adding a shot changes the total number of shots in this scene.\n2. We must recalculate the duration of every video shot to match the scene's voiceover.\n3. Your existing videos in this scene may need to be regenerated to maintain sync.\n\nDo you want to proceed?",
          performAdd
        )
      } else {
        performAdd()
      }
    }

    setShowAddShotModal(false)
  }

  const handleDeleteShot = (sceneId, shotId) => {
    const scene = scenes.find(scene => scene.id === sceneId)
    if (!scene) return

    const performDelete = () => {
      setScenes(prev =>
        prev.map(scene =>
          scene.id === sceneId
            ? { ...scene, shots: scene.shots.filter(s => s.id !== shotId) }
            : scene
        )
      )
      message.success('Shot deleted')
    }

    if (scene.shots.length === 1) {
      setConfirmationModalConfig({
        title: 'Delete Last Shot & Scene',
        content:
          'This is the last shot in the scene. Deleting it will remove the entire scene.\n\n1. This action will permanently delete the scene and all its content.\n2. This cannot be undone.\n\nAre you sure you want to proceed?',
        onConfirm: () => {
          setScenes(prev => prev.filter(s => s.id !== sceneId))
          message.success('Scene deleted')
          setShowConfirmationModal(false)
        }
      })
      setShowConfirmationModal(true)
      return
    }

    if (hasVidSrc(scene)) {
      showDurationWarning(
        'Delete Shot',
        'You are deleting a shot from a scene with generated videos.\n\n1. Removing a shot changes the timing of the remaining shots.\n2. We must recalculate the durations to match the voiceover.\n3. Existing videos in this scene may need to be regenerated.\n\nDo you want to proceed?',
        performDelete
      )
    } else {
      setConfirmationModalConfig({
        title: 'Delete Shot',
        content:
          'Are you sure you want to delete this shot? This action cannot be undone.',
        onConfirm: () => {
          performDelete()
          setShowConfirmationModal(false)
        }
      })
      setShowConfirmationModal(true)
    }
  }

  const handleDeleteScene = sceneId => {
    const scene = scenes.find(s => s.id === sceneId)
    if (!scene) return

    const hasVideoWarning = hasVidSrc(scene)
    const content = hasVideoWarning
      ? 'Warning: This scene has generated videos. Deleting it will remove all content and recalculate the overall video structure.\n\n1. All shots and generated videos within this scene will be permanently removed.\n2. This action cannot be undone.\n\nDo you want to proceed?'
      : 'You are about to delete this entire scene.\n\n1. All shots and content within this scene will be permanently removed.\n2. This action cannot be undone.\n\nDo you want to proceed?'

    setConfirmationModalConfig({
      title: 'Delete Scene',
      content: content,
      onConfirm: () => {
        setScenes(prev => prev.filter(scene => scene.id !== sceneId))
        message.success('Scene deleted')
        setShowConfirmationModal(false)
      }
    })
    setShowConfirmationModal(true)
  }

  const onClickGenerateAllImages = () => {
    Modal.confirm({
      title: 'Do you want to generate all images?',
      content: 'This will overwrite the current images.',
      centered: true,
      onOk() {
        generateAllImages(scenes, false)
      },
      onCancel() {
        console.log('Cancel')
      }
    })
  }

  const { isAnyImageGenerating, isAllImagesGenerated } = useMemo(() => {
    if (!scenes || !Array.isArray(scenes))
      return { isAnyImageGenerating: false, isAllImagesGenerated: false }

    const isAnyImageGenerating = scenes.some(scene =>
      scene.shots?.some(shot => shot?.isGenerating === true)
    )

    const isAllImagesGenerated = scenes.every(scene =>
      scene.shots?.every(shot => shot?.imageSrc)
    )

    return { isAnyImageGenerating, isAllImagesGenerated }
  }, [scenes])

  const imageGenerationStats = useMemo(() => {
    let total = 0
    let completed = 0
    let failed = 0
    let generating = 0
    let pending = 0

    scenes.forEach(scene => {
      scene.shots.forEach(shot => {
        total += 1

        if (shot?.isGenerating) {
          generating += 1
          return
        }

        if (shot?.error) {
          failed += 1
          return
        }

        if (shot?.imageSrc) {
          completed += 1
          return
        }

        pending += 1
      })
    })

    const progressPercent =
      total > 0 ? Math.round((completed / total) * 100) : 0

    let state = 'neutral'

    if (total === 0) {
      state = 'empty'
    } else if (generating > 0) {
      state = 'generating'
    } else if (failed > 0) {
      state = 'attention'
    } else if (completed === total) {
      state = 'complete'
    }

    return {
      total,
      completed,
      failed,
      generating,
      pending,
      progressPercent,
      state
    }
  }, [scenes])

  const hasFailedImages = imageGenerationStats.failed > 0
  const hasAnyShots = imageGenerationStats.total > 0

  const imageGenerationStatusExtra = useMemo(
    () => <ImageGenerationSummary stats={imageGenerationStats} />,
    [imageGenerationStats]
  )

  const getAllGeneratedImages = useMemo(() => {
    const images = []
    scenes.forEach(scene => {
      scene.shots.forEach(shot => {
        if (shot.imageSrc) {
          images.push({
            url: shot.imageSrc,
            sceneTitle: scene.title,
            shotTitle: shot.title
          })
        }
      })
    })
    return images
  }, [scenes])

  const getAllReferenceImages = useMemo(() => {
    const images = [...getAllGeneratedImages]

    if (formData.locationReference) {
      Object.entries(formData.locationReference).forEach(([key, value]) => {
        const url =
          typeof value === 'string'
            ? value
            : value?.uri || value?.url || value?.image || value?.src
        if (url) {
          images.push({
            url: url,
            sceneTitle: 'Location',
            shotTitle: key
          })
        }
      })
    }

    if (formData.characterList) {
      formData.characterList.forEach(char => {
        const url = char.attributes?.profile_pic
        if (url) {
          images.push({
            url: url,
            sceneTitle: 'Character',
            shotTitle: char.name
          })
        }
      })
    }

    return images
  }, [
    getAllGeneratedImages,
    formData.locationReference,
    formData.characterList
  ])

  const copyImageTargets = useMemo(() => {
    return scenes.flatMap(scene =>
      (scene.shots || []).map((shot, index) => ({
        value: `${scene.id}::${shot.id}`,
        sceneId: scene.id,
        shotId: shot.id,
        isGenerating: !!shot.isGenerating,
        imageSrc: shot.imageSrc || null,
        sceneTitle: scene.title || 'Scene',
        shotIndex: index + 1,
        shotTitle: shot.title || null,
        label: `${scene.title || 'Scene'} - Shot ${index + 1}${
          shot.title ? ` (${shot.title})` : ''
        }${shot.imageSrc ? ' - Has image' : ' - No image'}`
      }))
    )
  }, [scenes])

  const getCurrentEditingShotPrompt = () => {
    if (!currentEditingShot) return ''
    const scene = scenes.find(s => s.id === currentEditingShot.sceneId)
    const shot = scene?.shots.find(s => s.id === currentEditingShot.shotId)
    return shot?.initial_image_prompt || ''
  }

  const getCurrentEditingShotImage = () => {
    if (!currentEditingShot) return null
    const scene = scenes.find(s => s.id === currentEditingShot.sceneId)
    const shot = scene?.shots.find(s => s.id === currentEditingShot.shotId)
    return shot?.imageSrc || null
  }

  const handleSettingsDrawerClose = () => {
    setDrawerVisible(false)
    setSelectedSettings(null)
  }

  return (
    <div className="image-generation-container">
      <div className="header">
        <div className="header-title">
          <StepTitle
            iconType="picture"
            title="Image Generation"
            description="Create images for your video"
            showDivider={false}
          />
          <div className="header-summary">{imageGenerationStatusExtra}</div>
        </div>

        <div className="header-actions">
          <div className="view-mode-toggle" role="group" aria-label="Shot view mode">
            <Button
              size="small"
              className={`view-mode-btn ${shotViewMode === 'expanded' ? 'is-active' : ''}`}
              onClick={() => setShotViewMode('expanded')}
            >
              <Icon type="appstore" />
              Expanded
            </Button>
            <Button
              size="small"
              className={`view-mode-btn ${shotViewMode === 'compact' ? 'is-active' : ''}`}
              onClick={() => setShotViewMode('compact')}
            >
              <Icon type="bars" />
              Compact
            </Button>
          </div>
          {hasAnyShots && isAllImagesGenerated && (
            <Button
              className="action-btn primary-btn"
              icon="sync"
              loading={isAnyImageGenerating}
              onClick={onClickGenerateAllImages}
              disabled={isAnyImageGenerating}
            >
              Regenerate All
            </Button>
          )}
          {hasFailedImages && (
            <Button
              className="action-btn retry-btn"
              icon="reload"
              onClick={() => retryFailedImages(scenes)}
              disabled={isAnyImageGenerating}
            >
              Retry Failed
            </Button>
          )}
          <Button
            className="action-btn add-btn"
            icon="plus"
            onClick={addNewSceneWithShot}
          >
            Add Scene
          </Button>
        </div>
      </div>

      {scenes.map(scene => (
        <SceneContainer
          videoId={videoId}
          key={scene.id}
          scene={scene}
          isLoading={isLoading}
          formData={formData}
          updateSceneData={updateSceneData}
          setFormData={setFormData}
          addShotToScene={addShotToScene}
          handleDeleteScene={handleDeleteScene}
          handleDeleteShot={handleDeleteShot}
          handleRegenerateImage={handleRegenerateImage}
          handleRegenerateWithReference={handleRegenerateWithReference}
          handleRegenerateWithReferenceNanoBanana={
            handleRegenerateWithReferenceNanoBanana
          }
          handleEditSettings={handleEditSettings}
          updateShotDataRef={updateShotDataRef}
          handleSortEnd={handleSortEnd}
          handleCopyImageToShot={handleCopyImageToShot}
          voices={voices}
          loadingVoices={loadingVoices}
          generateVoiceForScene={generateVoiceForScene}
          onRefreshVoices={fetchVoices}
          isClusteringAudio={isClusteringAudio}
          onNavigateToStep={onNavigateToStep}
          audioClusteringProgress={activeAudioGenerationProgress}
          audioClusteringStatus={activeAudioGenerationStatus}
          handleFileUpload={handleFileUpload}
          isUploadingImage={isUploadingImage}
          copyImageTargets={copyImageTargets}
          isFetchingCharacters={isFetchingCharacters}
          onSceneStructureChange={handleSceneStructureChange}
          onStopGeneration={handleStopSceneAudioGeneration}
          onRestoreAudio={audioVersion =>
            handleRestoreAudio(scene.id, audioVersion)
          }
          shotViewMode={shotViewMode}
        />
      ))}

      <SettingsDrawer
        visible={drawerVisible}
        selectedSettings={selectedSettings}
        formData={formData}
        setFormData={setFormData}
        onClose={handleSettingsDrawerClose}
        updateSettings={updateSettings}
        saveSettings={saveSettings}
      />

      <ImageEditorModal
        visible={showImageEditorModal}
        mode={imageEditorMode}
        aspectRatio={formData.aspectRatio}
        onCancel={() => {
          setShowImageEditorModal(false)
          setCurrentEditingShot(null)
        }}
        onConfirm={handleImageEditorConfirm}
        initialPrompt={getCurrentEditingShotPrompt()}
        currentImage={getCurrentEditingShotImage()}
        referenceImages={getAllReferenceImages}
        videoId={videoId}
        creativeDirection={formData.creativeDirection}
      />

      <ConfirmationModal
        visible={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={confirmationModalConfig.onConfirm}
        title={confirmationModalConfig.title}
        content={confirmationModalConfig.content}
      />

      <AddShotModal
        visible={showAddShotModal}
        onCancel={() => setShowAddShotModal(false)}
        onConfirm={handleAddShotConfirm}
        isAddingScene={addShotContext.isAddingScene}
        locationReference={formData.locationReference || {}}
        locationMap={formData.locationMap || {}}
        characterList={formData.characterList || []}
        selectedCharacter={formData.selectedCharacter || []}
        outfitMap={formData.outfitMap || {}}
        outfitsReference={formData.outfits_reference || {}}
        outfits={formData.outfits || []}
        nextSceneIndex={scenes.length + 1}
        nextShotIndex={
          addShotContext.sceneId
            ? (scenes.find(s => s.id === addShotContext.sceneId)?.shots
                ?.length || 0) + 1
            : 1
        }
      />
    </div>
  )
}

export default ImageGeneration
